import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings as SettingsIcon, Palette, CreditCard, Upload, Loader2, Camera, User, Phone, Mail, Shield, KeyRound, Eye, EyeOff, Building2, Clock, Check, AlertTriangle, ExternalLink } from "lucide-react";
import BranchesSection from "@/components/settings/BranchesSection";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { plans } from "@/components/subscription/plans-data";

const COLORS = [
  { label: "أخضر", value: "#2E9E6E" },
  { label: "أزرق", value: "#3B82F6" },
  { label: "بنفسجي", value: "#8B5CF6" },
  { label: "وردي", value: "#EC4899" },
  { label: "برتقالي", value: "#F97316" },
  { label: "أحمر", value: "#EF4444" },
  { label: "سماوي", value: "#06B6D4" },
  { label: "كهرماني", value: "#F59E0B" },
];

/* ─────────────────── Parent Settings ─────────────────── */
const ParentSettings = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);

  const { data: profileData, isLoading, refetch } = useQuery({
    queryKey: ["parent-profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, phone, avatar_url")
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (profileData) {
      setFullName(profileData.full_name || "");
      setPhone(profileData.phone || "");
      setAvatarUrl(profileData.avatar_url || null);
    }
  }, [profileData]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: fullName.trim() || null,
        phone: phone.trim() || null,
        avatar_url: avatarUrl,
      })
      .eq("user_id", user.id);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم حفظ البيانات بنجاح" });
      refetch();
    }
    setSaving(false);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "خطأ", description: "يرجى اختيار ملف صورة", variant: "destructive" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "خطأ", description: "حجم الصورة يجب ألا يتجاوز 2 ميجابايت", variant: "destructive" });
      return;
    }
    setUploadingAvatar(true);
    const ext = file.name.split(".").pop();
    const filePath = `${user.id}/avatar-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from("child-photos").upload(filePath, file, { upsert: true });
    if (uploadError) {
      toast({ title: "خطأ في رفع الصورة", description: uploadError.message, variant: "destructive" });
      setUploadingAvatar(false);
      return;
    }
    const { data: signedData } = await supabase.storage.from("child-photos").createSignedUrl(filePath, 60 * 60 * 24 * 365);
    if (signedData?.signedUrl) {
      setAvatarUrl(signedData.signedUrl);
    }
    setUploadingAvatar(false);
    toast({ title: "تم رفع الصورة بنجاح" });
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast({ title: "يرجى ملء جميع الحقول", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "كلمة المرور يجب أن تكون 6 أحرف على الأقل", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "كلمتا المرور غير متطابقتين", variant: "destructive" });
      return;
    }
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم تغيير كلمة المرور بنجاح" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
    setChangingPassword(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const initials = fullName
    ? fullName.split(" ").map(w => w[0]).join("").slice(0, 2)
    : "👤";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">الإعدادات</h1>
        <p className="text-muted-foreground text-sm mt-1">إدارة حسابك الشخصي</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile Info */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              المعلومات الشخصية
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="w-20 h-20 border-2 border-border">
                  <AvatarImage src={avatarUrl || undefined} />
                  <AvatarFallback className="text-lg bg-primary/10 text-primary font-bold">{initials}</AvatarFallback>
                </Avatar>
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute -bottom-1 -left-1 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:opacity-90 transition-opacity"
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                </button>
                <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
              </div>
              <div>
                <p className="font-medium text-foreground">{fullName || "ولي الأمر"}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                الاسم الكامل
              </Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="الاسم الكامل" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                رقم الهاتف
              </Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xxxxxxxx" dir="ltr" className="text-right" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                البريد الإلكتروني
              </Label>
              <Input value={user?.email || ""} disabled className="bg-muted text-muted-foreground" />
              <p className="text-xs text-muted-foreground">لا يمكن تغيير البريد الإلكتروني</p>
            </div>

            <Button onClick={handleSaveProfile} className="gradient-hero border-0 w-full" disabled={saving || !fullName.trim()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التغييرات"}
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* Security */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                الأمان وكلمة المرور
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
                  كلمة المرور الجديدة
                </Label>
                <div className="relative">
                  <Input
                    type={showNewPass ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="كلمة المرور الجديدة (6 أحرف على الأقل)"
                    dir="ltr"
                    className="text-right pl-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPass(!showNewPass)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
                  تأكيد كلمة المرور
                </Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="أعد كتابة كلمة المرور"
                  dir="ltr"
                  className="text-right"
                />
              </div>

              <Button
                onClick={handleChangePassword}
                variant="outline"
                className="w-full"
                disabled={changingPassword || !newPassword || !confirmPassword}
              >
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Shield className="w-4 h-4 ml-2" />}
                تغيير كلمة المرور
              </Button>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <SettingsIcon className="w-5 h-5 text-primary" />
                معلومات الحساب
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">نوع الحساب</span>
                <span className="text-sm font-medium text-foreground bg-primary/10 text-primary px-3 py-1 rounded-full">ولي أمر</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">الحضانة</span>
                <span className="text-sm font-medium text-foreground">{profile?.nursery_id ? "مرتبط" : "غير مرتبط"}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">البريد الإلكتروني</span>
                <span className="text-sm font-medium text-foreground" dir="ltr">{user?.email}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────── Admin Settings ─────────────────── */
const AdminSettings = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const nurseryId = profile?.nursery_id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const subscription = useSubscription();

  // Nursery fields
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#2E9E6E");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Personal profile fields
  const [fullName, setFullName] = useState("");
  const [personalPhone, setPersonalPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Security fields
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);

  const { data: nursery, isLoading, refetch } = useQuery({
    queryKey: ["nursery-settings", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return null;
      const { data, error } = await supabase.from("nurseries").select("*").eq("id", nurseryId).single();
      if (error) throw error;
      return data;
    },
    enabled: !!nurseryId,
  });

  const { data: profileData, refetch: refetchProfile } = useQuery({
    queryKey: ["admin-profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, phone, avatar_url")
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (nursery) {
      setName(nursery.name || "");
      setAddress(nursery.address || "");
      setPhone(nursery.phone || "");
      setTaxNumber((nursery as any).tax_number || "");
      setPrimaryColor(nursery.primary_color || "#2E9E6E");
      setLogoUrl(nursery.logo_url || null);
    }
  }, [nursery]);

  useEffect(() => {
    if (profileData) {
      setFullName(profileData.full_name || "");
      setPersonalPhone(profileData.phone || "");
      setAvatarUrl(profileData.avatar_url || null);
    }
  }, [profileData]);

  const handleSave = async () => {
    if (!nurseryId) return;
    if (!name.trim() || !address.trim() || !phone.trim()) {
      toast({
        title: "بيانات ناقصة",
        description: "اسم الحضانة، العنوان، ورقم الهاتف حقول إلزامية",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("nurseries").update({
      name: name.trim(),
      address: address.trim(),
      phone: phone.trim(),
      tax_number: taxNumber.trim() || null,
      primary_color: primaryColor,
      logo_url: logoUrl,
    } as any).eq("id", nurseryId);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم حفظ التغييرات بنجاح" });
      refetch();
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !nurseryId) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "خطأ", description: "يرجى اختيار ملف صورة", variant: "destructive" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "خطأ", description: "حجم الصورة يجب أن لا يتجاوز 2 ميجابايت", variant: "destructive" });
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop();
    const filePath = `${nurseryId}/logo-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from("nursery-logos").upload(filePath, file, { upsert: true });
    if (uploadError) {
      toast({ title: "خطأ في رفع الصورة", description: uploadError.message, variant: "destructive" });
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("nursery-logos").getPublicUrl(filePath);
    setLogoUrl(urlData.publicUrl);
    setUploading(false);
    toast({ title: "تم رفع الشعار بنجاح" });
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "خطأ", description: "يرجى اختيار ملف صورة", variant: "destructive" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "خطأ", description: "حجم الصورة يجب ألا يتجاوز 2 ميجابايت", variant: "destructive" });
      return;
    }
    setUploadingAvatar(true);
    const ext = file.name.split(".").pop();
    const filePath = `${nurseryId || user.id}/avatar-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from("staff-photos").upload(filePath, file, { upsert: true });
    if (uploadError) {
      toast({ title: "خطأ في رفع الصورة", description: uploadError.message, variant: "destructive" });
      setUploadingAvatar(false);
      return;
    }
    const { data: signedData } = await supabase.storage.from("staff-photos").createSignedUrl(filePath, 60 * 60 * 24 * 365);
    if (signedData?.signedUrl) {
      setAvatarUrl(signedData.signedUrl);
    }
    setUploadingAvatar(false);
    toast({ title: "تم رفع الصورة بنجاح" });
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: fullName.trim() || null,
        phone: personalPhone.trim() || null,
        avatar_url: avatarUrl,
      })
      .eq("user_id", user.id);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم حفظ البيانات بنجاح" });
      refetchProfile();
    }
    setSavingProfile(false);
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast({ title: "يرجى ملء جميع الحقول", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "كلمة المرور يجب أن تكون 6 أحرف على الأقل", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "كلمتا المرور غير متطابقتين", variant: "destructive" });
      return;
    }
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم تغيير كلمة المرور بنجاح" });
      setNewPassword("");
      setConfirmPassword("");
    }
    setChangingPassword(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const initials = fullName
    ? fullName.split(" ").map(w => w[0]).join("").slice(0, 2)
    : "👤";

  const planName = plans.find(p => p.id === subscription.plan)?.name || subscription.plan || "غير محدد";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">إعدادات الحضانة</h1>
        <p className="text-muted-foreground text-sm mt-1">إدارة إعدادات الحضانة وحسابك الشخصي</p>
      </div>

      <Tabs defaultValue="nursery" dir="rtl">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="nursery" className="gap-1.5">
            <Building2 className="w-4 h-4" />
            الحضانة
          </TabsTrigger>
          <TabsTrigger value="profile" className="gap-1.5">
            <User className="w-4 h-4" />
            الحساب الشخصي
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-1.5">
            <Shield className="w-4 h-4" />
            الأمان
          </TabsTrigger>
        </TabsList>

        {/* ── Nursery Tab ── */}
        <TabsContent value="nursery">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <SettingsIcon className="w-5 h-5 text-primary" />
                  المعلومات الأساسية
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(!nursery?.address || !nursery?.phone) && (
                  <div className="p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      بيانات الحضانة غير مكتملة. يجب إدخال <strong>العنوان</strong> و<strong>رقم الهاتف</strong> لإكمال التسجيل.
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>اسم الحضانة *</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم الحضانة" required />
                </div>
                <div className="space-y-2">
                  <Label>العنوان *</Label>
                  <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="المدينة - الشارع" required />
                </div>
                <div className="space-y-2">
                  <Label>رقم الهاتف *</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xxxxxxxx" dir="ltr" className="text-right" required />
                </div>
                <div className="space-y-2">
                  <Label>الرقم الضريبي (اختياري)</Label>
                  <Input value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} placeholder="123-456-789" dir="ltr" className="text-right" />
                </div>
                <Button
                  onClick={handleSave}
                  className="gradient-hero border-0 w-full"
                  disabled={saving || !name.trim() || !address.trim() || !phone.trim()}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التغييرات"}
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Camera className="w-5 h-5 text-primary" />
                    شعار الحضانة
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted shrink-0">
                      {logoUrl ? (
                        <img src={logoUrl} alt="شعار الحضانة" className="w-full h-full object-cover rounded-xl" />
                      ) : (
                        <Upload className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="text-sm text-muted-foreground">ارفع شعار حضانتك (PNG, JPG - حد أقصى 2 ميجابايت)</p>
                      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Upload className="w-4 h-4 ml-2" />}
                        {uploading ? "جاري الرفع..." : "اختيار صورة"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5 text-primary" />
                    اللون الرئيسي
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-3">
                    {COLORS.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setPrimaryColor(c.value)}
                        className={`h-12 rounded-lg transition-all flex items-center justify-center text-xs font-medium ${
                          primaryColor === c.value ? "ring-2 ring-offset-2 ring-ring scale-105" : "hover:scale-105"
                        }`}
                        style={{ backgroundColor: c.value, color: "#fff" }}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <Label className="shrink-0">لون مخصص:</Label>
                    <Input
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <span className="text-sm text-muted-foreground font-mono">{primaryColor}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Subscription - Real Data */}
              <Card className={`shadow-card ${
                subscription.isTrial
                  ? "border-blue-500/50 bg-blue-50/30 dark:bg-blue-950/10"
                  : subscription.isExpired
                    ? "border-destructive/50 bg-destructive/5"
                    : subscription.isActive
                      ? "border-green-500/50 bg-green-50/30 dark:bg-green-950/10"
                      : ""
              }`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    الاشتراك
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {subscription.loading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        {subscription.isTrial ? (
                          <Clock className="w-6 h-6 text-blue-600 shrink-0" />
                        ) : subscription.isActive ? (
                          <Check className="w-6 h-6 text-green-600 shrink-0" />
                        ) : (
                          <AlertTriangle className="w-6 h-6 text-destructive shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="font-bold text-foreground">
                            {subscription.isTrial ? "فترة تجربة مجانية" : planName}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {subscription.isTrial
                              ? `تنتهي التجربة خلال ${subscription.daysRemaining ?? 0} يوم`
                              : subscription.isActive
                                ? subscription.daysRemaining !== null
                                  ? `ينتهي خلال ${subscription.daysRemaining} يوم`
                                  : "اشتراك نشط"
                                : subscription.isExpired
                                  ? "اشتراكك منتهي - يرجى التجديد"
                                  : subscription.isSuspended
                                    ? "الاشتراك معلق"
                                    : "لا يوجد اشتراك"}
                          </p>
                        </div>
                        {subscription.daysRemaining !== null && subscription.daysRemaining <= 7 && (
                          <Badge variant="destructive" className="text-xs px-2 py-1 shrink-0">
                            {subscription.daysRemaining === 0 ? "ينتهي اليوم!" : `باقي ${subscription.daysRemaining} يوم`}
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() => navigate("/dashboard/subscription")}
                      >
                        <ExternalLink className="w-4 h-4" />
                        إدارة الاشتراك
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Branches */}
              {nurseryId && (
                <BranchesSection nurseryId={nurseryId} nurseryName={name || "الحضانة الرئيسية"} />
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Profile Tab ── */}
        <TabsContent value="profile">
          <div className="max-w-xl mx-auto">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  المعلومات الشخصية
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Avatar */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Avatar className="w-20 h-20 border-2 border-border">
                      <AvatarImage src={avatarUrl || undefined} />
                      <AvatarFallback className="text-lg bg-primary/10 text-primary font-bold">{initials}</AvatarFallback>
                    </Avatar>
                    <button
                      onClick={() => avatarInputRef.current?.click()}
                      className="absolute -bottom-1 -left-1 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:opacity-90 transition-opacity"
                      disabled={uploadingAvatar}
                    >
                      {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                    </button>
                    <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{fullName || "المدير"}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                    الاسم الكامل
                  </Label>
                  <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="الاسم الكامل" />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                    رقم الهاتف
                  </Label>
                  <Input value={personalPhone} onChange={(e) => setPersonalPhone(e.target.value)} placeholder="05xxxxxxxx" dir="ltr" className="text-right" />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                    البريد الإلكتروني
                  </Label>
                  <Input value={user?.email || ""} disabled className="bg-muted text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">لا يمكن تغيير البريد الإلكتروني</p>
                </div>

                <Button onClick={handleSaveProfile} className="gradient-hero border-0 w-full" disabled={savingProfile || !fullName.trim()}>
                  {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التغييرات"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Security Tab ── */}
        <TabsContent value="security">
          <div className="max-w-xl mx-auto">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  الأمان وكلمة المرور
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
                    كلمة المرور الجديدة
                  </Label>
                  <div className="relative">
                    <Input
                      type={showNewPass ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="كلمة المرور الجديدة (6 أحرف على الأقل)"
                      dir="ltr"
                      className="text-right pl-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPass(!showNewPass)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
                    تأكيد كلمة المرور
                  </Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="أعد كتابة كلمة المرور"
                    dir="ltr"
                    className="text-right"
                  />
                </div>

                <Button
                  onClick={handleChangePassword}
                  variant="outline"
                  className="w-full"
                  disabled={changingPassword || !newPassword || !confirmPassword}
                >
                  {changingPassword ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Shield className="w-4 h-4 ml-2" />}
                  تغيير كلمة المرور
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* ─────────────────── Main Settings Page ─────────────────── */
const SettingsPage = () => {
  const { roles } = useAuth();
  const isParent = roles.includes("parent") && !roles.includes("admin") && !roles.includes("teacher");

  return isParent ? <ParentSettings /> : <AdminSettings />;
};

export default SettingsPage;
